import { Chip, CircularProgress, Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import { useUpdateUserStatus } from "../../hooks/useAccessManagement";

// Status chip that actually toggles. It used to be display-only, which read as broken —
// the chip looks like a control, so clicking it should do what the dialog's switch does.
// Confirmed first, since disabling signs the person out and blocks them from signing in.
export default function StatusCell({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const updateStatus = useUpdateUserStatus();

  const handleToggle = async () => {
    const next = !user.isActive;

    const result = await Swal.fire({
      title: next ? "Enable access?" : "Disable access?",
      html: `<div style="font-size: 14px; font-family: 'Quicksand'">
          ${next
            ? `<b>${user.fullName}</b> will be able to sign in again.`
            : `<b>${user.fullName}</b> will be signed out and blocked from signing in until re-enabled.`}
        </div>`,
      icon: next ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: next ? "Yes, enable" : "Yes, disable",
      cancelButtonText: "Cancel",
      confirmButtonColor: next ? "#09AC31" : "#E34C14",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await updateStatus.mutateAsync({ id: user.id, isActive: next });
      // The endpoint refuses to strand the system without an active Super Admin, and
      // refuses self-disable — both come back as success:false with a reason.
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(next ? "Access enabled" : "Access disabled", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not update status", { variant: "error" });
    }
  };

  if (updateStatus.isLoading) {
    return <CircularProgress size={14} sx={{ color: "#FF5200" }} />;
  }

  return (
    <Tooltip title={user.isActive ? "Click to disable this user" : "Click to enable this user"}>
      <Chip
        size="small"
        label={user.isActive ? "Active" : "Disabled"}
        onClick={handleToggle}
        sx={{
          fontFamily: "Quicksand",
          fontWeight: 700,
          fontSize: 11,
          cursor: "pointer",
          bgcolor: user.isActive ? "rgba(9,172,49,0.12)" : "rgba(227,76,20,0.10)",
          color: user.isActive ? "#09AC31" : "#E34C14",
          transition: "filter 0.15s ease",
          "&:hover": { filter: "brightness(0.94)" },
        }}
      />
    </Tooltip>
  );
}
